import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  splitLink,
  TRPCClientError,
} from '@trpc/client';
import type { HTTPBatchLinkOptions, inferRouterClient } from '@trpc/client';

import type { AppRouter } from '@asas/api/trpc';
import type { AnfrageEntschieden } from '@asas/domain';
import { istOptimisticLockConflict } from '@asas/domain/konflikt';
import type { OptimisticLockConflict } from '@asas/domain/konflikt';
import { istApImportAbweisung } from '@asas/domain/ap-import-gruende';
import type { ApImportAbweisung } from '@asas/domain/ap-import-gruende';
import { RESTAUFWAND_ABWEISUNGEN } from '@asas/domain/restaufwand';
import type { RestaufwandAbweisung } from '@asas/domain/restaufwand';

/**
 * Typsicherer Zugriff auf die API (AC-010).
 *
 * `import type` fuer den Router: der Compiler entfernt die Zeile vollstaendig,
 * es fliesst ausschliesslich der TYP. Zwei Schranken sichern das ab, beide
 * geprueft:
 *
 *   1. `apps/api/package.json` exportiert `./trpc` NUR mit einer
 *      `types`-Bedingung, ohne `default`. Ein tatsaechlich benutzter
 *      Wertimport laesst `vite build` abbrechen:
 *      „./trpc is not exported under the conditions [module, browser, …]".
 *   2. `@typescript-eslint/consistent-type-imports` steht auf Fehlerstufe.
 *
 * NICHT VERWECHSELN: die Schranke sitzt im BUILD, nicht im Typecheck.
 * `pnpm typecheck` bleibt bei einem Wertimport gruen, weil
 * `apps/web/tsconfig.json` `apps/api` als Projektreferenz fuehrt und
 * TypeScript dann auf die Quelldateien umleitet. `pnpm build` ist Teil der
 * Pruefkette und laeuft in CI — dort faellt es auf.
 *
 * BATCHING SEIT AC-016, und das ist kein Komfortthema, sondern Last auf der
 * Datenbank: Der `SessionAuthenticator` arbeitet bewusst OHNE positiven Cache,
 * damit ein Logout beim naechsten Request wirkt (AC-006). Jeder einzelne
 * tRPC-Aufruf kostet deshalb eine Sitzungspruefung in einer eigenen
 * RLS-Transaktion. Acht Kacheln einer Ansicht erzeugten mit `httpLink` acht
 * davon, mit `httpBatchLink` eine — nachgemessen in `trpc.batching.spec.ts`.
 *
 * WAS DER WECHSEL KOSTET — zwei Dinge, beide gemessen.
 *
 * ERSTENS, und das ist die unangenehmere: In einem Stapel wirkt ein
 * `AbortSignal` eines EINZELNEN Aufrufs nicht mehr. tRPC fuehrt die Signale
 * aller Aufrufe eines Stapels zusammen und bricht erst ab, wenn ALLE
 * abgebrochen sind. Nachgemessen gegen 11.18.0: Wird einer von zwei
 * gebuendelten Aufrufen abgebrochen, kommt er trotzdem `fulfilled` mit dem
 * Serverergebnis zurueck.
 *
 * Folge fuer die Oberflaeche: Ein abgebrochener Aufruf liefert ein Ergebnis,
 * das vielleicht niemand mehr sehen wollte — wer auf Abbruch setzt, um eine
 * ueberholte Anfrage loszuwerden, bekommt sie doch. Und die Fehlerart
 * `abgebrochen` in `daten/fehler.ts` greift nur bei einem Aufruf, der allein
 * im Stapel war.
 *
 * Seit AC-402 benutzt das Wochenraster ein `AbortSignal` und laeuft deshalb
 * ueber eine eigene, ungebuendelte Link-Kette (`EIGENE_LINK_KETTE` unten). Wer
 * das naechste braucht — eine Suche, die bei jedem Tastendruck neu laedt, ist
 * der uebliche Fall —, entscheidet dort zwischen „Ergebnis verwerfen statt
 * Anfrage abbrechen" und einem Eintrag in dieser Liste. `trpc.batching.spec.ts`
 * haelt das gemessene Verhalten fest, damit eine kuenftige tRPC-Fassung es
 * nicht still aendert.
 *
 * ZWEITENS: In einem Stapel wartet die schnellste Antwort auf die langsamste,
 * weil alle zusammen zurueckkommen. Bei einer
 * Ansicht, die eine 26-Wochen-Aggregation (ADR-07) neben mehreren kleinen
 * Abfragen laedt, verzoegert die grosse alle anderen. Faellt das auf, ist die
 * Antwort NICHT, das Batching abzuschalten, sondern die langsame Prozedur in
 * eine eigene Link-Kette oder hinter `maxItems` zu legen.
 *
 * KEIN Transformer: was auf der Leitung liegt, ist lesbares JSON. Zeitpunkte
 * kommen als ISO-String, Geld und Stunden als String (siehe README,
 * „Drahtformat").
 *
 * 401 UND 403 kommen seit AC-007 in der tRPC-Fehlerform an. Vorher antwortete
 * NEST mit `{"statusCode":401,…}`, weil der globale `TenantGuard` wirft, bevor
 * tRPC ueberhaupt laeuft — der Client scheiterte dann beim Auswerten, und der
 * Anwender sah „etwas ist schiefgelaufen" statt „bitte neu anmelden".
 * `TrpcExceptionFilter` in `apps/api` uebersetzt das jetzt.
 *
 * EIN EIGENER LINK IST DAFUER NICHT NOETIG, und das ist eine Entscheidung mit
 * Begruendung: die AC-010-Uebergabe hatte einen vorgeschlagen, weil der Client
 * die Nest-Form nicht auswerten konnte. Seit die Form stimmt, traegt der
 * normale `TRPCClientError` alles Noetige — ein Link daneben waere eine zweite
 * Stelle, an der derselbe Zustand erkannt wird, und die beiden liefen
 * auseinander. Stattdessen zwei Funktionen unten, die JEDER Aufrufer benutzen
 * kann.
 */

/** Gleiche Quelle wie `api.ts`. Keine zweite Konfigurationsstelle daneben. */
const configuredBase: unknown = import.meta.env.VITE_API_BASE_URL;
const baseUrl = (typeof configuredBase === 'string' ? configuredBase : '').replace(/\/$/, '');

export const TRPC_URL = `${baseUrl}/api/trpc`;

/**
 * Ausdrueckliche Typannotation, nicht Inferenz.
 *
 * Ohne sie meldet TypeScript TS2883: der inferierte Typ liesse sich nur unter
 * Verweis auf Dateien in `apps/api/node_modules` benennen, die `apps/web`
 * unter einem anderen Pfad kennt.
 */

/**
 * Schickt das Sitzungscookie mit.
 *
 * `HTTPLinkOptions` kennt keine `credentials`-Option, deshalb ein eigener
 * `fetch`. Wie in `api.ts`: die Sitzung haengt am Cookie, nicht an einem
 * Header. Ohne `include` schickt der Browser es bei einem anderen Ursprung
 * nicht mit — im Dev-Betrieb faellt das wegen des Vite-Proxys nicht auf, in
 * Produktion schon.
 *
 * Der Typ wird aus `HTTPLinkOptions` ABGELEITET und nicht hingeschrieben:
 * `FetchEsque` selbst ist in `@trpc/client` intern und nicht exportiert. Dessen
 * `init` erlaubt auch `RequestInitEsque`, und das
 * traegt `signal?: AbortSignal | undefined`, waehrend der DOM-`RequestInit`
 * unter `exactOptionalPropertyTypes` dort `AbortSignal | null` verlangt.
 *
 * Deshalb wird das Optionsobjekt aus BENANNTEN Feldern gebaut und nicht
 * gestreut: `signal` muss von `undefined` auf `null` normalisiert werden, und
 * beim Streuen bliebe genau dieser Fall unsichtbar.
 */
type TrpcFetch = NonNullable<HTTPBatchLinkOptions<AppRouter['_def']['_config']['$types']>['fetch']>;

const asasFetch: TrpcFetch = (url, init) =>
  fetch(url, {
    method: init?.method ?? 'GET',
    headers: init?.headers ?? {},
    body: init?.body ?? null,
    signal: init?.signal ?? null,
    credentials: 'include',
  });

/**
 * Obergrenze fuer die URL eines gebuendelten LESEZUGRIFFS, in Zeichen.
 *
 * Gebuendelte Abfragen reisen als GET, und die Eingaben aller Aufrufe stehen
 * dabei IN DER URL. tRPC teilt den Stapel selbsttaetig auf, sobald diese Grenze
 * erreicht ist; ohne Angabe buendelt es unbegrenzt und die Anfrage wird
 * irgendwann von der Gegenseite abgewiesen — als 431 oder als abgerissene
 * Verbindung, also gerade nicht als auswertbarer tRPC-Fehler.
 *
 * Der Wert ist NACHGEMESSEN und nicht geschaetzt. Massgeblich ist Nodes
 * Kopfzeilen-Budget, in das die Anfragezeile mit hineinzaehlt:
 *
 *     $ node -e "console.log(require('http').maxHeaderSize)"
 *     16384            # Node 24.21.0, der Pin aus .nvmrc
 *
 * 8000 laesst davon mehr als die Haelfte fuer Cookie, Herkunft und die
 * uebrigen Kopfzeilen uebrig. Caddy ist hier NICHT die engere Grenze (Go
 * erlaubt 1 MB), Node ist es.
 *
 * SCHREIBZUGRIFFE reisen als POST, ihre Eingaben stehen im KOERPER und nicht in
 * der URL — `maxURLLength` begrenzt sie deshalb NICHT. Fuer sie gilt allein
 * `BODY_LIMIT` = 1 MB aus `apps/api/src/bootstrap.ts`, und zwar fuer den
 * GANZEN Stapel zusammen.
 *
 * WAS DAS IM FEHLERFALL BEDEUTET, offen benannt: Eine 413 aus dem Body-Parser
 * entsteht VOR dem Controller und geht am `TrpcExceptionFilter` vorbei (dort
 * ausdruecklich vermerkt). Der Client kann sie nicht auspacken, und
 * `klassifiziereFehler` stuft sie als `netz` ein — bei einer Mutation also mit
 * `moeglicherweiseAusgefuehrt: true`, obwohl NICHTS geschrieben wurde. Die
 * Warnung ist damit zu vorsichtig, nicht zu lasch: Sie raet zum Nachsehen, wo
 * nichts nachzusehen waere. Das ist die richtige Richtung fuer einen Irrtum,
 * aber es ist einer.
 *
 * Heute unerreichbar fuer den Stapel — keine gebuendelte Prozedur nimmt
 * annaehernd so viel entgegen. Die einzige grosse Schreibprozedur, die
 * Vorschau des AP-Imports (AC-302), laeuft ungebuendelt mit eigenem Limit
 * (`EIGENE_LINK_KETTE`, `bootstrap.ts`); ihre Datei prueft die Ansicht vor dem
 * Senden gegen die Grenze.
 */
export const MAX_URL_LENGTH = 8000;

/**
 * Prozeduren mit EIGENER, ungebuendelter Link-Kette (AC-402, E11).
 *
 * Die 26-Wochen-Aggregation des Wochenrasters ist der Fall, den der Kommentar
 * oben vorhersagt: Im Stapel wartete jede kleine Abfrage auf sie, und ein
 * Fensterwechsel waehrend des Ladens liesse sich nicht abbrechen. Ueber
 * `httpLink` ist sie eine eigene Anfrage, und ihr `AbortSignal` wirkt —
 * festgehalten in `trpc.batching.spec.ts`. Kosten: eine eigene
 * Sitzungspruefung je Laden (AC-006, kein positiver Cache).
 *
 * Seit AC-302 auch die Vorschau des AP-Imports: sie traegt die Datei (bis 5 MB
 * als base64), und nur ungebuendelt gilt am Server das grosse Limit — im Stapel
 * bekaeme es jede andere Prozedur mit, und kleine Abfragen warteten auf den
 * Upload.
 *
 * Seit AC-407 auch `auswertungen.fachgruppenSummen`: die Fachgruppen-Summen
 * aggregieren bis zu 26 Wochen ueber ALLE Gruppen des Mandanten, und ein
 * Fensterwechsel waehrend des Ladens soll die vorige Anfrage wirklich abbrechen
 * (`auslastung/auslastung.ts`, `erzeugeSummenLader`) — im Stapel wirkte der
 * `AbortSignal` nicht, und jede kleine Abfrage wartete auf die Aggregation.
 *
 * Bewusst eine geschlossene Liste, kein Muster wie „alles unter
 * `wochenraster.`": die Aufschluesselung einer Zelle ist klein und bleibt im
 * Stapel, ebenso `workPackageImport.uebernehmen`.
 */
export const EIGENE_LINK_KETTE: ReadonlySet<string> = new Set([
  'wochenraster.raster',
  'workPackageImport.vorschau',
  'auswertungen.fachgruppenSummen',
]);

export const trpc: inferRouterClient<AppRouter> = createTRPCClient<AppRouter>({
  links: [
    splitLink({
      condition: (op) => EIGENE_LINK_KETTE.has(op.path),
      true: httpLink({ url: TRPC_URL, fetch: asasFetch }),
      false: httpBatchLink({
        url: TRPC_URL,
        maxURLLength: MAX_URL_LENGTH,
        // `HTTPBatchLinkOptions` kennt keine `credentials`-Option, deshalb ueber
        // einen eigenen `fetch`. Wie in `api.ts`: die Sitzung haengt am Cookie,
        // nicht an einem Header. Ohne `include` schickt der Browser es bei einem
        // anderen Ursprung nicht mit — im Dev-Betrieb faellt das wegen des
        // Vite-Proxys nicht auf, in Produktion schon.
        fetch: asasFetch,
      }),
    }),
  ],
});

/**
 * Zwei Zustaende, die eine Oberflaeche verschieden behandeln muss (AC-007).
 *
 *   nicht angemeldet  ->  zur Anmeldung schicken; erneutes Versuchen hilft
 *   kein Zugriff      ->  Hinweis zeigen; erneutes Versuchen hilft NICHT
 *
 * Beide als Funktion und nicht als `error.data.code === '…'` an jeder
 * Aufrufstelle: der Vergleich stuende sonst verstreut im Code, und die erste
 * Stelle, die ihn falsch schreibt, meldet stillschweigend den falschen
 * Zustand.
 *
 * Bewusst OHNE Auswertung der Meldung — die ist fuer Menschen und darf sich
 * aendern, ohne dass Programmlogik bricht. Massgeblich ist `data.code`.
 *
 * SEIT AC-016 EXPORTIERT, und zwar genau deshalb: `daten/fehler.ts` braucht
 * mehr Faelle als die beiden Zustaende unten. Wuerde es den Vergleich selbst
 * schreiben, gaebe es zwei Stellen, an denen derselbe Zustand erkannt wird —
 * und die erste, die ihn falsch schreibt, meldet stillschweigend den falschen.
 * Der Extraktor bleibt deshalb einer.
 *
 * `null` heisst NICHT „kein Fehler", sondern „kein auswertbarer tRPC-Fehler":
 * ein Netzabbruch, eine abgerissene Antwort, ein Aufruf, der den Server nie
 * erreicht hat. `daten/fehler.ts` wertet genau das aus.
 */
export function trpcFehlerCode(error: unknown): string | null {
  if (!(error instanceof TRPCClientError)) {
    return null;
  }

  const code: unknown = (error.data as { code?: unknown } | undefined)?.code;

  return typeof code === 'string' ? code : null;
}

/**
 * Beide Werte eines Versionskonflikts (AC-409), aus `error.data.konflikt` —
 * oder `null`, wenn der Fehler keiner ist. Wie `trpcFehlerCode` der einzige
 * Extraktor; die Huelle wird geprueft, denn sie kommt aus dem Netz.
 */
export function trpcKonflikt(error: unknown): OptimisticLockConflict | null {
  if (trpcFehlerCode(error) !== 'CONFLICT' || !(error instanceof TRPCClientError)) {
    return null;
  }

  const konflikt: unknown = (error.data as { konflikt?: unknown } | undefined)?.konflikt;

  return istOptimisticLockConflict(konflikt) ? konflikt : null;
}

/**
 * Wer eine Anfrage schon entschieden oder zurueckgezogen hat (AC-405, AK 5,
 * AK 6), aus `error.data.anfrageEntschieden` — oder `null`. Neben
 * `trpcFehlerCode` und `trpcKonflikt` der einzige Extraktor dafuer; die Huelle
 * wird ohne Zod geprueft (apps/web/CLAUDE.md, Domaene im Browser), denn sie
 * kommt aus dem Netz.
 */
export function trpcAnfrageEntschieden(error: unknown): AnfrageEntschieden | null {
  if (trpcFehlerCode(error) !== 'CONFLICT' || !(error instanceof TRPCClientError)) {
    return null;
  }

  const wert: unknown = (error.data as { anfrageEntschieden?: unknown } | undefined)
    ?.anfrageEntschieden;
  if (typeof wert !== 'object' || wert === null) return null;

  const { anfrageId, ergebnis, entschiedenAm, entschiedenVon } = wert as Record<string, unknown>;
  const von = entschiedenVon as Record<string, unknown> | null | undefined;

  return typeof anfrageId === 'string' &&
    (ergebnis === 'freigegeben' || ergebnis === 'abgelehnt' || ergebnis === 'zurueckgezogen') &&
    typeof entschiedenAm === 'string' &&
    typeof von === 'object' &&
    von !== null &&
    typeof von.id === 'string' &&
    (typeof von.name === 'string' || von.name === null)
    ? { anfrageId, ergebnis, entschiedenAm, entschiedenVon: { id: von.id, name: von.name } }
    : null;
}

/**
 * Warum der Server einen Import abgewiesen hat (AC-302), aus
 * `error.data.apImport.grund` — oder `null`. Neben `trpcFehlerCode` der einzige
 * Extraktor dafuer; ohne Zod geprueft, denn es kommt aus dem Netz.
 */
export function trpcApImportGrund(error: unknown): ApImportAbweisung | null {
  const code = trpcFehlerCode(error);

  if ((code !== 'BAD_REQUEST' && code !== 'CONFLICT') || !(error instanceof TRPCClientError)) {
    return null;
  }

  const wert: unknown = (error.data as { apImport?: unknown } | undefined)?.apImport;
  const grund: unknown =
    typeof wert === 'object' && wert !== null ? (wert as { grund?: unknown }).grund : undefined;

  return istApImportAbweisung(grund) ? grund : null;
}

/**
 * Warum der Server eine Restaufwandsmeldung abgewiesen hat (AC-601), aus
 * `error.data.restaufwand.grund` — oder `null`. Neben `trpcFehlerCode` der
 * einzige Extraktor dafuer; ohne Zod geprueft, denn es kommt aus dem Netz.
 */
export function trpcRestaufwandGrund(error: unknown): RestaufwandAbweisung | null {
  if (trpcFehlerCode(error) !== 'BAD_REQUEST' || !(error instanceof TRPCClientError)) {
    return null;
  }

  const wert: unknown = (error.data as { restaufwand?: unknown } | undefined)?.restaufwand;
  const grund: unknown =
    typeof wert === 'object' && wert !== null ? (wert as { grund?: unknown }).grund : undefined;

  return RESTAUFWAND_ABWEISUNGEN.find((bekannt) => bekannt === grund) ?? null;
}

/** Die Sitzung fehlt oder ist abgelaufen. */
export function istNichtAngemeldet(error: unknown): boolean {
  return trpcFehlerCode(error) === 'UNAUTHORIZED';
}

/**
 * Der Server hat den Aufruf abgelehnt, obwohl ein Zugang vorlag.
 *
 * ACHTUNG BEI DER BESCHRIFTUNG IN DER OBERFLAECHE: Das ist NICHT
 * gleichbedeutend mit „dir fehlt ein Recht". Der Server antwortet aus mehreren
 * Gruenden mit 403, und sie sind hier absichtlich nicht unterscheidbar:
 *
 *   - das noetige Recht fehlt (AC-007)            -> erneut versuchen hilft nie
 *   - die Sitzung gehoert zu einem abgeschalteten
 *     Mandanten oder zu keinem (AC-005, AC-006)   -> neu anmelden hilft
 *   - der `Origin` der Anfrage ist nicht erlaubt
 *     (CSRF-Schutz fuer Mutationen, AC-006)       -> ein Aufbaufehler
 *
 * Die Unterscheidung fehlt nicht aus Nachlaessigkeit: sie waere ein Orakel fuer
 * jemanden, der Mandanten-Ids oder Rechte raet — dieselbe Linie wie im
 * `TenantGuard`. Eine Oberflaeche formuliert deshalb neutral („diese Funktion
 * steht dir nicht zur Verfuegung") und bietet den Weg zur Neuanmeldung an,
 * statt ihn auszuschliessen.
 */
export function istOhneZugriff(error: unknown): boolean {
  return trpcFehlerCode(error) === 'FORBIDDEN';
}
